<template>
  <div class="modal fade show" style="display: block;" tabindex="-1">
    <div class="modal-backdrop-custom"></div>

    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content shadow-lg">
        <!-- Modal Header -->
        <div class="modal-header bg-gradient-primary text-white">
          <div>
            <h5 class="modal-title mb-0">
              <i class="mdi mdi-folder-open me-2"></i>Category Details
            </h5>
            <small class="opacity-75">View complete information</small>
          </div>
          <button @click="$emit('close')" type="button" class="btn-close btn-close-white"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-4">
          <!-- Category Header -->
          <div class="card border-0 bg-light mb-4">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h4 class="mb-1">{{ category.categoryName }}</h4>
                  <span :class="getStatusBadgeClass(category.status)">
                    {{ category.status }}
                  </span>
                </div>
                <div class="text-end">
                  <div class="avatar-lg">
                    <div class="avatar-title bg-primary bg-gradient rounded-circle">
                      <i class="mdi mdi-folder font-size-24"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row mb-4">
            <div class="col-md-4 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-account-multiple text-primary font-size-24 mb-2"></i>
                  <h3 class="mb-0">{{ category.currentUsers || 0 }}</h3>
                  <small class="text-muted">Current Users</small>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-account-check text-success font-size-24 mb-2"></i>
                  <h3 class="mb-0">{{ category.usersLimit || '∞' }}</h3>
                  <small class="text-muted">User Limit</small>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center">
                  <i class="mdi mdi-shield-account text-info font-size-24 mb-2"></i>
                  <h3 class="mb-0">{{ getRolesCount() }}</h3>
                  <small class="text-muted">Assigned Roles</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div v-if="category.usersLimit > 0" class="card border-0 shadow-sm mb-4">
            <div class="card-body">
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Capacity</span>
                <span class="font-weight-bold">
                  {{ category.currentUsers || 0 }} / {{ category.usersLimit }}
                  ({{ getCapacityPercentage() }}%)
                </span>
              </div>
              <div class="progress" style="height: 10px;">
                <div
                    class="progress-bar"
                    :class="getProgressBarClass()"
                    :style="{width: getCapacityPercentage() + '%'}"
                    role="progressbar"
                ></div>
              </div>
            </div>
          </div>

          <!-- Category Information -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0">
                <i class="mdi mdi-information me-2"></i>Category Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Category ID</label>
                  <p class="mb-0"><strong>{{ category.id }}</strong></p>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Category Name</label>
                  <p class="mb-0"><strong>{{ category.categoryName }}</strong></p>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Status</label>
                  <p class="mb-0">
                    <span :class="getStatusBadgeClass(category.status)">
                      {{ category.status }}
                    </span>
                  </p>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="text-muted small">User Limit</label>
                  <p class="mb-0">
                    <strong>{{ category.usersLimit > 0 ? category.usersLimit : 'Unlimited' }}</strong>
                  </p>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Created At</label>
                  <p class="mb-0">{{ formatDateTime(category.createdAt) }}</p>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Last Updated</label>
                  <p class="mb-0">{{ formatDateTime(category.updatedAt) }}</p>
                </div>

                <div v-if="category.description" class="col-md-12 mb-3">
                  <label class="text-muted small">Description</label>
                  <p class="mb-0">{{ category.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Assigned Roles -->
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-light">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="mdi mdi-shield-account me-2"></i>Assigned Roles
                  <span class="badge bg-primary ms-2">{{ getRolesCount() }}</span>
                </h6>
                <button @click="$emit('manage-roles', category)" class="btn btn-sm btn-primary">
                  <i class="mdi mdi-pencil me-1"></i>Manage
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="category.roles && category.roles.length > 0" class="roles-list">
                <div class="d-flex flex-wrap gap-2">
                  <span
                      v-for="(role, index) in category.roles"
                      :key="index"
                      class="badge bg-light text-dark border"
                  >
                    {{ role }}
                  </span>
                </div>
              </div>
              <div v-else class="text-center text-muted py-3">
                <i class="mdi mdi-shield-off font-size-24 mb-2 d-block"></i>
                No roles assigned yet
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <button @click="$emit('close')" type="button" class="btn btn-light">
            <i class="mdi mdi-close me-1"></i>Close
          </button>
          <button @click="$emit('manage-roles', category)" type="button" class="btn btn-info">
            <i class="mdi mdi-shield-account me-1"></i>Manage Roles
          </button>
          <button @click="$emit('edit', category)" type="button" class="btn btn-primary">
            <i class="mdi mdi-pencil me-1"></i>Edit Category
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "ViewCategoryModal",
  props: {
    category: {
      type: Object,
      required: true
    }
  },
  methods: {
    getRolesCount() {
      return this.category.roles ? this.category.roles.length : 0;
    },

    getCapacityPercentage() {
      if (!this.category.usersLimit || this.category.usersLimit === 0) {
        return 0;
      }
      const percentage = (this.category.currentUsers / this.category.usersLimit) * 100;
      return Math.min(Math.round(percentage), 100);
    },

    getProgressBarClass() {
      const percentage = this.getCapacityPercentage();
      if (percentage >= 90) return 'bg-danger';
      if (percentage >= 75) return 'bg-warning';
      if (percentage >= 50) return 'bg-info';
      return 'bg-success';
    },

    getStatusBadgeClass(status) {
      return status === 'active' ? 'badge bg-success' : 'badge bg-secondary';
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      return moment(datetime).format('DD MMM YYYY, HH:mm');
    }
  }
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.35);
}

.modal {
  z-index: 1050;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.avatar-lg {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.roles-list {
  max-height: 200px;
  overflow-y: auto;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.8em;
}

.font-size-24 {
  font-size: 24px;
}
</style>